import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Euro, Zap, Crown, Navigation, Shield, Building2, ExternalLink } from "lucide-react";
import { useJobAiSummary, JobAiPreviewCard } from "./JobAiPreview";
import { jobCoverSrc, categoryCover } from "../lib/covers";

function timeAgo(iso) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "Hoje";
  if (d === 1) return "Ontem";
  return `${d}d`;
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";
}

/**
 * Thumbnail quadrada com logo da empresa (fallback para iniciais).
 */
function JobThumb({ job, size, isGold }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const logoUrl = job?.company_id ? `${backend}/api/companies/${job.company_id}/logo` : null;
  const [logoFailed, setLogoFailed] = useState(false);

  const border = isGold ? "border-amber-400/70" : "border-black/10";
  const bg = isGold ? "bg-slate-900" : "bg-slate-50";
  const textColor = isGold ? "text-amber-300" : "text-slate-500";

  const showLogo = logoUrl && !logoFailed;

  return (
    <div className={`${size} shrink-0 rounded-lg border ${border} ${bg} overflow-hidden flex items-center justify-center`}>
      {showLogo ? (
        <img
          src={logoUrl}
          alt={`Logo ${job?.company_name || ""}`.trim()}
          loading="lazy"
          onError={() => setLogoFailed(true)}
          className="w-full h-full object-contain"
        />
      ) : (
        <span className={`font-heading font-black tracking-tighter ${textColor} text-base sm:text-lg`}>
          {initials(job?.company_name)}
        </span>
      )}
    </div>
  );
}

/**
 * Cover da vaga com fallback automático — usado no modo grid.
 */
function JobCoverThumb({ job, tallerCover }) {
  const [src, setSrc] = useState(() => jobCoverSrc(job));
  const fallback = categoryCover(job?.category, job?.id || job?.slug);
  const height = tallerCover ? "h-40 sm:h-44" : "h-32 sm:h-36";

  return (
    <div className={`relative w-full ${height} bg-slate-100 overflow-hidden`}>
      <img
        src={src}
        alt={`Foto ilustrativa ${job?.category || ""}`.trim()}
        loading="lazy"
        onError={() => { if (src !== fallback) setSrc(fallback); }}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        data-testid={`job-cover-${job?.slug || job?.id || "x"}`}
      />
      {/* Gradiente subtil para leitura da badge */}
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/25 to-transparent pointer-events-none" />
    </div>
  );
}

/** Cover quadrada (para o modo lista) — mesma fonte de imagem do grid, nunca falha.
 *  Sobrepõe um pequeno logótipo da empresa quando existe. */
function JobCoverSquare({ job, size, isGold }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const [src, setSrc] = useState(() => jobCoverSrc(job));
  const [logoOk, setLogoOk] = useState(true);
  const fallback = categoryCover(job?.category, job?.id || job?.slug);
  const ring = isGold ? "ring-1 ring-amber-400/70" : "";
  const logoUrl = job?.company_id ? `${backend}/api/companies/${job.company_id}/logo` : null;
  return (
    <div className={`relative ${size} shrink-0 rounded-lg overflow-hidden bg-slate-100 ${ring}`}>
      <img
        src={src}
        alt={`Foto ilustrativa ${job?.category || ""}`.trim()}
        loading="lazy"
        onError={() => { if (src !== fallback) setSrc(fallback); }}
        className="w-full h-full object-cover"
        data-testid={`job-thumb-${job?.slug || job?.id || "x"}`}
      />
      {logoUrl && logoOk && (
        <img
          src={logoUrl}
          alt={`Logótipo ${job?.company_name || ""}`.trim()}
          loading="lazy"
          onError={() => setLogoOk(false)}
          className="absolute bottom-1 left-1 w-6 h-6 rounded-md bg-white object-contain shadow-md ring-1 ring-black/10 p-0.5"
          data-testid={`job-logo-${job?.slug || job?.id || "x"}`}
        />
      )}
    </div>
  );
}

/** Skeleton loader — utiliza a mesma forma dos cards para transição suave. */
export const JobCardSkeleton = ({ variant = "list" }) => {
  if (variant === "grid") {
    return (
      <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden">
        <div className="w-full h-32 sm:h-36 bg-slate-200 animate-pulse" />
        <div className="p-3 space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
          <div className="h-4 w-4/5 bg-slate-200 rounded animate-pulse" />
          <div className="h-3 w-3/5 bg-slate-200 rounded animate-pulse" />
          <div className="flex gap-2 pt-2">
            <div className="h-5 w-14 bg-slate-200 rounded-full animate-pulse" />
            <div className="h-5 w-16 bg-slate-200 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 sm:gap-4 bg-white rounded-xl border border-black/5 shadow-sm p-3 sm:p-3.5">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-slate-200 animate-pulse shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-1/3 bg-slate-200 rounded animate-pulse" />
        <div className="h-4 w-4/5 bg-slate-200 rounded animate-pulse" />
        <div className="h-3 w-3/5 bg-slate-200 rounded animate-pulse" />
        <div className="flex gap-2 pt-1">
          <div className="h-5 w-14 bg-slate-200 rounded-full animate-pulse" />
          <div className="h-5 w-16 bg-slate-200 rounded-full animate-pulse" />
        </div>
      </div>
    </div>
  );
};

export const JobCard = ({ job, compact = false, variant }) => {
  // Auto-detect variant: se `compact=true` e não é dado variant, usar "grid" (novo layout visual)
  const layout = variant || (compact ? "grid" : "list");
  const tier = job.boost?.tier;
  const isGold = tier === "gold" || tier === "destaque_max";
  const isSilver = tier === "silver" || tier === "destaque";
  const isExpress = tier === "express";
  const [hovered, setHovered] = useState(false);
  const { loading, bullets, request, cancel } = useJobAiSummary(job.slug);
  const isExternal = job.source_type && job.source_type !== "internal" && job.source_type !== "iefp";

  const accent = isGold ? "before:bg-amber-400"
    : isSilver ? "before:bg-sky-400"
    : isExpress ? "before:bg-orange-400"
    : "before:bg-transparent";

  const border = isGold ? "border-amber-400/60 hover:border-amber-400"
    : isSilver ? "border-sky-300/60 hover:border-sky-400"
    : isExpress ? "border-orange-300/60 hover:border-orange-400"
    : "border-black/5 hover:border-slate-300";

  // ===== LAYOUT: GRID (visual com foto de capa em cima) =====
  if (layout === "grid") {
    return (
      <div
        className="relative"
        onMouseEnter={() => { setHovered(true); request(); }}
        onMouseLeave={() => { setHovered(false); cancel(); }}
        onFocus={() => { setHovered(true); request(); }}
        onBlur={() => { setHovered(false); cancel(); }}
      >
        <Link
          to={`/empregos/${job.slug}`}
          data-testid={`job-card-${job.slug}`}
          className={`group relative flex flex-col bg-white rounded-xl border ${border} shadow-sm hover:shadow-lg transition overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:z-10 ${accent}`}
        >
          {/* Cover */}
          <JobCoverThumb job={job} tallerCover={isGold} />

          {/* Badges sobrepostas ao topo do cover */}
          <div className="absolute top-2.5 left-3 right-3 flex items-start justify-between gap-2 z-10">
            <div className="flex flex-wrap gap-1.5">
              {(isGold || isSilver || isExpress) && (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide shadow-sm ${
                    isGold ? "bg-amber-400 text-slate-950"
                      : isSilver ? "bg-sky-500 text-white"
                      : "bg-orange-500 text-white"
                  }`}
                  data-testid={isGold ? "badge-gold" : isSilver ? "badge-silver" : "badge-express"}
                >
                  {isGold && <Crown className="w-3 h-3" />}
                  {isSilver && <Shield className="w-3 h-3" />}
                  {isExpress && <Zap className="w-3 h-3" />}
                  {isGold ? "Gold" : isSilver ? "Silver" : "Express"}
                </span>
              )}
              {isExternal && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/85 backdrop-blur-sm text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  <ExternalLink className="w-2.5 h-2.5" /> {job.source_label || "Externa"}
                </span>
              )}
            </div>
            <span className="shrink-0 inline-flex items-center rounded-full bg-white/90 backdrop-blur-sm text-slate-700 px-2 py-0.5 text-[10px] font-semibold">
              {timeAgo(job.created_at)}
            </span>
          </div>

          {/* Logo flutuante */}
          <div className="absolute left-3 -mt-6 z-10">
            <JobThumb job={job} size="w-11 h-11 shadow-md" isGold={isGold} />
          </div>

          {/* Info */}
          <div className="p-3 pl-3.5 pt-8 flex-1 flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {job.company_name}
            </p>
            <h3 className="font-heading font-bold tracking-tight text-slate-900 line-clamp-2 text-[15px] sm:text-base leading-snug mt-1">
              {job.title}
            </h3>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1 min-w-0 max-w-full truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{job.concelho}, {job.district}</span>
              </span>
              {typeof job.distance_km === "number" && (
                <span data-testid="job-distance" className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 px-1.5 py-0.5 text-[10px] font-semibold">
                  <Navigation className="w-3 h-3" />
                  {job.distance_km === 0 ? "aqui" : `${job.distance_km}km`}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3 shrink-0" /> {job.regime}
              </span>
            </div>

            {/* Chips + salário */}
            <div className="flex items-end justify-between gap-2 mt-auto pt-3">
              <div className="flex flex-wrap gap-1 min-w-0">
                <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium truncate">
                  {job.category}
                </span>
                <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium">
                  {job.work_mode}
                </span>
              </div>
              {job.salary_min && (
                <span className="inline-flex items-center gap-1 text-[12px] font-bold text-slate-800 shrink-0">
                  <Euro className="w-3 h-3" />
                  {job.salary_min}{job.salary_max ? `–${job.salary_max}` : "+"}
                </span>
              )}
            </div>
          </div>
        </Link>
        {hovered && <JobAiPreviewCard loading={loading} bullets={bullets} isGold={isGold} />}
      </div>
    );
  }

  // ===== LAYOUT: LIST (thumb pequena à esquerda + info à direita) =====
  const thumbSize = "w-16 h-16 sm:w-20 sm:h-20";
  return (
    <div
      className="relative"
      onMouseEnter={() => { setHovered(true); request(); }}
      onMouseLeave={() => { setHovered(false); cancel(); }}
      onFocus={() => { setHovered(true); request(); }}
      onBlur={() => { setHovered(false); cancel(); }}
    >
      <Link
        to={`/empregos/${job.slug}`}
        data-testid={`job-card-${job.slug}`}
        className={`group relative flex items-start gap-3 sm:gap-4 bg-white rounded-xl border ${border} shadow-sm hover:shadow-md transition p-3 sm:p-3.5 overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${accent}`}
      >
        <JobCoverSquare job={job} size={thumbSize} isGold={isGold} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">
                {job.company_name}
              </p>
              <h3 className="font-heading font-bold tracking-tight text-slate-900 line-clamp-2 mt-0.5 text-[15px] sm:text-base leading-snug">
                {job.title}
              </h3>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {(isGold || isSilver || isExpress) && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                  isGold ? "bg-amber-400 text-slate-950"
                  : isSilver ? "bg-sky-500 text-white"
                  : "bg-orange-500 text-white"
                }`}
                data-testid={isGold ? "badge-gold" : isSilver ? "badge-silver" : "badge-express"}>
                  {isGold && <Crown className="w-3 h-3" />}
                  {isSilver && <Shield className="w-3 h-3" />}
                  {isExpress && <Zap className="w-3 h-3" />}
                  {isGold ? "Gold" : isSilver ? "Silver" : "Express"}
                </span>
              )}
              {isExternal && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                  <ExternalLink className="w-2.5 h-2.5" /> {job.source_label || "Externa"}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] sm:text-xs text-slate-600">
            <span className="inline-flex items-center gap-1 min-w-0 max-w-full truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{job.concelho}, {job.district}</span>
            </span>
            {typeof job.distance_km === "number" && (
              <span data-testid="job-distance" className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 px-1.5 py-0.5 text-[10px] font-semibold">
                <Navigation className="w-3 h-3" />
                {job.distance_km === 0 ? "aqui" : `${job.distance_km}km`}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3 shrink-0" />
              <span className="truncate">{job.regime}</span>
            </span>
            {job.salary_min && (
              <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                <Euro className="w-3 h-3 shrink-0" />
                {job.salary_min}{job.salary_max ? `–${job.salary_max}` : "+"}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex flex-wrap gap-1 min-w-0">
              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium truncate">
                {job.category}
              </span>
              <span className="hidden sm:inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-medium">
                {job.work_mode}
              </span>
            </div>
            <span className="shrink-0 text-[10px] sm:text-[11px] text-slate-400 font-medium">
              {timeAgo(job.created_at)}
            </span>
          </div>
        </div>
      </Link>
      {hovered && <JobAiPreviewCard loading={loading} bullets={bullets} isGold={isGold} />}
    </div>
  );
};
